import React, { useState } from 'react';
import { Button, message, Modal, Space, Table, Upload } from 'antd';
import type { TableColumnsType, UploadFile } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
    LIST_PDA_SCAN_SOUND_FILES,
    ListPdaScanSoundFilesResponse,
    PdaScanSoundFile,
    UPDATE_PDA_SCAN_SOUND_FILE,
    UPLOAD_PDA_SCAN_SOUND,
    UpdatePdaScanSoundFileResponse,
    UploadPdaScanSoundResponse,
} from '../graphql/pdaScanConfigQueries';

const ScanSoundFileTable: React.FC = () => {
    const { t } = useTranslation();
    const { data, loading, refetch } = useQuery<ListPdaScanSoundFilesResponse>(LIST_PDA_SCAN_SOUND_FILES, {
        fetchPolicy: 'network-only',
    });
    const [editingSoundId, setEditingSoundId] = useState<string | null>(null);

    const dataSource: PdaScanSoundFile[] = data?.listPdaScanSoundFiles ?? [];

    const columns: TableColumnsType<PdaScanSoundFile> = [
        {
            title: t('pdaScanConfig.soundFile.soundId'),
            dataIndex: 'soundId',
            key: 'soundId',
            width: 140,
        },
        {
            title: t('pdaScanConfig.soundFile.soundName'),
            key: 'soundName',
            width: 200,
            render: (_, record) => t(`pdaScanConfig.soundName.${record.soundId}`, { defaultValue: record.soundId }),
        },
        {
            title: t('pdaScanConfig.soundFile.soundUrl'),
            key: 'url',
            render: (_, record) => record.url
                ? (
                    <a href={record.url} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                        {record.url}
                    </a>
                )
                : <span style={{ color: '#999' }}>-</span>,
        },
        {
            title: t('pdaScanConfig.soundFile.actions'),
            key: 'actions',
            width: 140,
            render: (_, record) => (
                <Button type="link" onClick={() => setEditingSoundId(record.soundId)}>
                    {t('pdaScanConfig.soundFile.replaceMp3')}
                </Button>
            ),
        },
    ];

    return (
        <>
            <Table<PdaScanSoundFile>
                rowKey="soundId"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={false}
            />
            {editingSoundId && (
                <ReplaceMp3Modal
                    soundId={editingSoundId}
                    onClose={() => setEditingSoundId(null)}
                    onSuccess={async () => {
                        await refetch();
                        setEditingSoundId(null);
                    }}
                />
            )}
        </>
    );
};

interface ReplaceMp3ModalProps {
    soundId: string;
    onClose: () => void;
    onSuccess: () => void;
}

const ReplaceMp3Modal: React.FC<ReplaceMp3ModalProps> = ({ soundId, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [file, setFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [updateSoundFile] = useMutation<UpdatePdaScanSoundFileResponse>(UPDATE_PDA_SCAN_SOUND_FILE);
    const [uploadSound] = useMutation<UploadPdaScanSoundResponse>(UPLOAD_PDA_SCAN_SOUND);

    const handleSubmit = async () => {
        if (!file) {
            message.warning(t('pdaScanConfig.soundFile.pleaseChooseFile'));
            return;
        }
        setSubmitting(true);
        try {
            // 1) 读 File → base64（不含 data:audio/mpeg;base64, 前缀）
            const fileBase64 = await fileToBase64(file);

            // 2) 调后端 uploadPdaScanSound：后端代上传到公共桶，绕开浏览器 → R2 直传的 CORS 限制
            const uploadRes = await uploadSound({
                variables: {
                    input: {
                        fileBase64,
                        filename: file.name,
                        contentType: file.type || 'audio/mpeg',
                    },
                },
            });
            const objectKey = uploadRes.data?.uploadPdaScanSound?.objectKey;
            if (!objectKey) {
                throw new Error('Failed to upload mp3');
            }

            // 3) 回写 object_key 到 DB
            await updateSoundFile({ variables: { soundId, objectKey } });

            message.success(t('pdaScanConfig.soundFile.replaceSuccess'));
            onSuccess();
        } catch (e) {
            console.error('Replace mp3 failed', e);
            message.error(t('pdaScanConfig.soundFile.replaceFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open
            title={t('pdaScanConfig.soundFile.replaceMp3Title', { soundId })}
            onCancel={onClose}
            onOk={handleSubmit}
            okButtonProps={{ loading: submitting, disabled: !file }}
            cancelButtonProps={{ disabled: submitting }}
            maskClosable={false}
            destroyOnClose
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                <Upload.Dragger
                    accept="audio/mpeg,.mp3"
                    multiple={false}
                    maxCount={1}
                    showUploadList={!!file}
                    fileList={file ? ([{ uid: '-1', name: file.name, status: 'done' } as UploadFile]) : []}
                    beforeUpload={(f) => {
                        setFile(f);
                        return false; // 阻止 antd 默认上传，统一在 onOk 走 R2
                    }}
                    onRemove={() => {
                        setFile(null);
                        return true;
                    }}
                >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">{t('pdaScanConfig.soundFile.uploadHint')}</p>
                </Upload.Dragger>
                {file && (
                    <audio controls src={URL.createObjectURL(file)} style={{ width: '100%' }} />
                )}
            </Space>
        </Modal>
    );
};

// File → 纯 base64 字符串（去掉 data:xxx;base64, 前缀），与后端 uploadPdaScanSound 协议对齐
const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
        reader.onload = () => {
            const result = reader.result as string;
            const commaIdx = result.indexOf(',');
            resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
        };
        reader.readAsDataURL(file);
    });

export default ScanSoundFileTable;
